/* ============================================= */
/* DISTRICT OPERATIONAL ENGINE                   */
/* Pure aggregation — zero API calls             */
/* Composable with all pastoral-feed/intelligence */
/* primitives. Ready for district assistant,      */
/* predictive alerts, and scheduling engine.     */
/* ============================================= */

import { Church } from "lucide-react";
import { evaluateChurchRisk, VISIT_THRESHOLDS } from "./pastoral-intelligence";
import {
  FEED_PRIORITY,
  normalizeFeedItem,
  buildPrayerFeed,
  buildEventFeed,
  mergeOperationalFeed,
} from "./pastoral-feed";
import { getDaysSince } from "./date";
import {
  WORKFLOW_TYPES,
  WORKFLOW_STAGES,
  WORKFLOW_URGENCY,
  evaluateWorkflowHealth,
  computeWorkflowUrgency,
  buildWorkflowFeed,
} from "./pastoral-workflows";

/* ============================= */
/* DISTRICT SIGNAL TYPES         */
/* ============================= */

export const DISTRICT_SIGNAL_TYPES = {
  CHURCH_STALE:          "CHURCH_STALE",           // church with no visit > neglected threshold
  CHURCH_HIGH_RISK:      "CHURCH_HIGH_RISK",        // church risk level critical/high (non-visit reason)
  PRAYER_URGENT_CLUSTER: "PRAYER_URGENT_CLUSTER",   // cluster of urgent prayers across district
  LOW_ACTIVITY_CLUSTER:  "LOW_ACTIVITY_CLUSTER",    // multiple stale churches in same district
};

/* ============================= */
/* CHURCH FEED STYLES            */
/* Static — no dynamic Tailwind  */
/* ============================= */

const CHURCH_CRITICAL_STYLE = {
  icon:               Church,
  iconContainerClass: "bg-red-50 dark:bg-red-950/30",
  iconClass:          "text-red-600 dark:text-red-400",
};

const CHURCH_HIGH_STYLE = {
  icon:               Church,
  iconContainerClass: "bg-orange-50 dark:bg-orange-950/30",
  iconClass:          "text-orange-600 dark:text-orange-400",
};

/* ============================= */
/* VISIT RECENCY AGGREGATION     */
/* O(n) — single pass, no N+1   */
/* ============================= */

/**
 * Computes visit recency and count per church from a flat visits array.
 * No per-church fetches — all data derived from one district-level request.
 *
 * Returns: {
 *   visitRecencyMap: Record<church_id, daysSince>,
 *   visitCountMap:   Record<church_id, count>
 * }
 */
export function computeChurchVisitStats(visits) {
  const latestDate = {};
  const counts     = {};

  for (const v of visits) {
    if (!v.church_id) continue;
    counts[v.church_id] = (counts[v.church_id] || 0) + 1;
    if (!v.date) continue;
    if (!latestDate[v.church_id] || v.date > latestDate[v.church_id]) {
      latestDate[v.church_id] = v.date;
    }
  }

  const visitRecencyMap = Object.fromEntries(
    Object.entries(latestDate).map(([id, date]) => [id, getDaysSince(date)])
  );

  return { visitRecencyMap, visitCountMap: counts };
}

/* ============================= */
/* CHURCH RISK EVALUATION        */
/* ============================= */

/**
 * Runs evaluateChurchRisk for every church in the district.
 * Uses data already in memory — no extra fetches.
 *
 * Returns: Array<{ church, lastVisitDays, risk: EvaluatorResult }>
 */
export function evaluateChurchesForDistrict(
  churches,
  { visitRecencyMap = {}, visitCountMap = {}, eventsCountMap = {} } = {}
) {
  return churches.map((church) => {
    const lastVisitDays = visitRecencyMap[church.id] ?? null;
    const visitsCount   = visitCountMap[church.id]   ?? 0;
    const eventsCount   = eventsCountMap[church.id]  ?? 0;

    const risk = evaluateChurchRisk({ lastVisitDays, eventsCount, visitsCount });

    return { church, lastVisitDays, risk };
  });
}

/* ============================= */
/* DISTRICT SIGNAL AGGREGATION   */
/* Cross-entity intelligence     */
/* ============================= */

/**
 * Aggregates district-level signals from church evaluations + prayers.
 *
 * Cross-entity prioritization: a cluster of stale churches accumulates
 * higher score than any single stale entity — naturally elevating systemic
 * problems above isolated ones.
 *
 * Returns: Array<DistrictSignal> sorted by score desc.
 * Ready for: notification batching, assistant recommendations, trend detection.
 */
export function buildDistrictSignals(churchEvaluations, prayers = []) {
  const signals = [];

  const urgentPrayers = prayers.filter(
    (p) => p.priority === "alta" && p.status === "activa"
  );

  // Per-church signals — only critical/high risk qualify
  for (const { church, risk, lastVisitDays } of churchEvaluations) {
    if (risk.level !== "critical" && risk.level !== "high") continue;

    const isStale =
      lastVisitDays !== null && lastVisitDays > VISIT_THRESHOLDS.neglected;

    signals.push({
      type:       isStale ? DISTRICT_SIGNAL_TYPES.CHURCH_STALE : DISTRICT_SIGNAL_TYPES.CHURCH_HIGH_RISK,
      severity:   risk.level,
      entityType: "church",
      entityId:   church.id,
      entityName: church.name,
      score:      risk.score,
      meta:       { lastVisitDays },
    });
  }

  // Cross-church: urgent prayer cluster
  if (urgentPrayers.length > 0) {
    signals.push({
      type:       DISTRICT_SIGNAL_TYPES.PRAYER_URGENT_CLUSTER,
      severity:   urgentPrayers.length >= 3 ? "critical" : "high",
      entityType: "district",
      entityId:   null,
      entityName: null,
      score:      Math.min(urgentPrayers.length * 12, 48),
      meta:       { count: urgentPrayers.length },
    });
  }

  // Cross-church: low activity cluster (2+ stale churches = systemic risk)
  const staleChurches = churchEvaluations.filter(
    ({ lastVisitDays }) =>
      lastVisitDays !== null && lastVisitDays > VISIT_THRESHOLDS.neglected
  );
  if (staleChurches.length > 1) {
    signals.push({
      type:       DISTRICT_SIGNAL_TYPES.LOW_ACTIVITY_CLUSTER,
      severity:   staleChurches.length >= 4 ? "critical" : "high",
      entityType: "district",
      entityId:   null,
      entityName: null,
      score:      Math.min(staleChurches.length * 10, 40),
      meta:       { count: staleChurches.length },
    });
  }

  signals.sort((a, b) => b.score - a.score);
  return signals;
}

/* ============================= */
/* DISTRICT PRIORITIES           */
/* Compatible with PriorityStack */
/* ============================= */

const PRIORITY_LEVEL_ORDER = { critical: 0, high: 1, medium: 2 };

/**
 * Converts district signals into actionable priorities.
 * Each item includes `route` for navigation without requiring an explicit actionMap.
 * Semantically deduped: prayer cluster and activity cluster appear once each,
 * regardless of how many signals contributed.
 *
 * Shape compatible with PriorityStack (key + message + level).
 * Extended shape for district: adds `route` for routing in District.jsx.
 *
 * Returns: Array<{ key, message, level, route }>
 */
export function buildDistrictPriorities(signals) {
  const seen       = new Set();
  const priorities = [];

  for (const sig of signals) {
    let item = null;

    switch (sig.type) {
      case DISTRICT_SIGNAL_TYPES.CHURCH_STALE: {
        const days = sig.meta.lastVisitDays;
        item = {
          key:     `church-stale-${sig.entityId}`,
          message: `${sig.entityName}: sin visita (${days ?? "—"} días)`,
          level:   sig.severity === "critical" ? "critical" : "high",
          route:   `/churches/${sig.entityId}`,
        };
        break;
      }

      case DISTRICT_SIGNAL_TYPES.CHURCH_HIGH_RISK:
        item = {
          key:     `church-risk-${sig.entityId}`,
          message: `${sig.entityName}: requiere atención pastoral`,
          level:   "high",
          route:   `/churches/${sig.entityId}`,
        };
        break;

      case DISTRICT_SIGNAL_TYPES.PRAYER_URGENT_CLUSTER: {
        if (seen.has("prayers-urgent")) break;
        const n = sig.meta.count;
        item = {
          key:     "prayers-urgent",
          message: `${n} oración${n !== 1 ? "es" : ""} urgente${n !== 1 ? "s" : ""} en el distrito`,
          level:   sig.severity === "critical" ? "critical" : "high",
          route:   "/prayers",
        };
        break;
      }

      case DISTRICT_SIGNAL_TYPES.LOW_ACTIVITY_CLUSTER:
        if (seen.has("low-activity")) break;
        item = {
          key:     "low-activity",
          message: `${sig.meta.count} iglesias sin actividad reciente`,
          level:   "medium",
          route:   "/churches",
        };
        break;

      default:
        break;
    }

    if (item && !seen.has(item.key)) {
      seen.add(item.key);
      priorities.push(item);
    }
  }

  priorities.sort(
    (a, b) =>
      (PRIORITY_LEVEL_ORDER[a.level] ?? 3) - (PRIORITY_LEVEL_ORDER[b.level] ?? 3)
  );

  return priorities;
}

/* ============================= */
/* DISTRICT CHURCH FEED          */
/* ============================= */

/**
 * Converts at-risk church evaluations into FeedItems.
 * Only critical/high risk — healthy churches create visual noise.
 * entityType/entityId/route populated for future assistant consumption.
 */
export function buildDistrictChurchFeed(churchEvaluations, { nav } = {}) {
  return churchEvaluations
    .filter(({ risk }) => risk.level === "critical" || risk.level === "high")
    .map(({ church, risk, lastVisitDays }) => {
      const style =
        risk.level === "critical" ? CHURCH_CRITICAL_STYLE : CHURCH_HIGH_STYLE;
      const feedPriority =
        risk.level === "critical" ? FEED_PRIORITY.critical : FEED_PRIORITY.warning;

      return normalizeFeedItem({
        id:                 `church-risk-${church.id}`,
        type:               "church-risk",
        ...style,
        title:              church.name,
        subtitle:
          lastVisitDays !== null
            ? `Sin visita hace ${lastVisitDays} días`
            : "Sin registro de visitas",
        priority:           feedPriority,
        _date:              null,
        onClick:            nav ? () => nav(`/churches/${church.id}`) : undefined,
        entityType:         "church",
        entityId:           church.id,
        route:              `/churches/${church.id}`,
      });
    });
}

/* ============================= */
/* DISTRICT OPERATIONAL FEED     */
/* ============================= */

/**
 * Merges church risks + prayers + events into one sorted, deduped feed.
 * max 10 items — pastoral attention span; pagination extension point exists
 * via mergeOperationalFeed's limit option.
 *
 * Events route via church_id when available (falls back to /agenda).
 */
export function buildDistrictFeed(churchEvaluations, prayers, events, { nav } = {}) {
  const churchFeed = buildDistrictChurchFeed(churchEvaluations, { nav });
  const prayerFeed = buildPrayerFeed(prayers, { nav });
  const eventFeed  = buildEventFeed(events, {
    nav,
    getPath: (e) =>
      e.church_id ? `/churches/${e.church_id}/events/${e.id}` : "/agenda",
  });

  return mergeOperationalFeed([churchFeed, prayerFeed, eventFeed], { limit: 10 });
}

/* ============================= */
/* DISTRICT WORKFLOW AGGREGATION  */
/* Cross-entity coordination     */
/* Ready for district assistant   */
/* ============================= */

/**
 * Derives CHURCH_RECOVERY workflows for all at-risk churches in the district.
 * Uses a simplified momentum proxy derived from visit recency + count alone
 * (full per-church buildPastoralMemory would require N+1 fetch for full history).
 *
 * Output is compatible with buildWorkflowFeed() and mergeOperationalFeed().
 *
 * Designed for:
 * - District assistant context building
 * - Smart cross-church scheduling
 * - Future AI recommendation generation
 *
 * NOT wired to District.jsx yet — district already surfaces church risks via
 * buildDistrictChurchFeed. Use this when adding the district assistant layer.
 *
 * @param {Array} churchEvaluations — from evaluateChurchesForDistrict()
 * @param {{ nav? }}
 * @returns {Workflow[]}
 */
export function buildDistrictWorkflows(churchEvaluations, { nav } = {}) {
  const workflows = [];

  for (const { church, lastVisitDays } of churchEvaluations) {
    if (lastVisitDays === null || lastVisitDays <= VISIT_THRESHOLDS.neglected) continue;

    // Proxy momentum from recency alone (no full visit history at district level)
    const trend = lastVisitDays > 120 ? "stable_neglected"
                : lastVisitDays > 90  ? "declining"
                :                       "unknown";

    const proxyMomentum = {
      trend,
      visits_90d: 0,
      visits_30d: 0,
      avg_cadence: null,
      confidence: 0.5,
    };

    const { continuityScore, stage } = evaluateWorkflowHealth(proxyMomentum, {
      daysSince:  lastVisitDays,
      visitCount: 0,
    });

    workflows.push({
      type:               WORKFLOW_TYPES.CHURCH_RECOVERY,
      entityType:         "church",
      entityId:           church.id,
      stage,
      momentum:           trend,
      urgency:            computeWorkflowUrgency(continuityScore, lastVisitDays),
      continuityScore,
      lastActionAt:       null,
      daysSinceLastAction: lastVisitDays,
      nextSuggestedAction: stage === WORKFLOW_STAGES.STALLED
        ? "Retomar el seguimiento a la iglesia"
        : "Programar visita pastoral",
      contextLabel:        stage === WORKFLOW_STAGES.STALLED
        ? "El seguimiento perdió continuidad"
        : "Sin seguimiento registrado aún",
      meta:               { churchName: church.name },
    });
  }

  // Sort: most urgent first, then longest absence
  workflows.sort((a, b) => {
    const ua = a.urgency === WORKFLOW_URGENCY.CRITICAL ? 0
             : a.urgency === WORKFLOW_URGENCY.HIGH     ? 1
             : a.urgency === WORKFLOW_URGENCY.MEDIUM   ? 2 : 3;
    const ub = b.urgency === WORKFLOW_URGENCY.CRITICAL ? 0
             : b.urgency === WORKFLOW_URGENCY.HIGH     ? 1
             : b.urgency === WORKFLOW_URGENCY.MEDIUM   ? 2 : 3;
    if (ua !== ub) return ua - ub;
    return (b.daysSinceLastAction ?? 0) - (a.daysSinceLastAction ?? 0);
  });

  return workflows;
}
